import classnames from 'classnames';
import { debounce } from 'lodash';
import React, { useCallback, useEffect, useState } from 'react';
import { Trans, useTranslation } from 'react-i18next';

import { useFormatCurrency, useMobile } from '@krea/common/hooks';
import { settings } from '@krea/common/settings';
import Button from '@krea/common/shared-components/button';
import TextInput from '@krea/common/shared-components/fields/input';
import Radio from '@krea/common/shared-components/fields/radio';
import RadioGroup from '@krea/common/shared-components/fields/radiogroup';
import { Helper } from '@krea/common/shared-components/helper';
import { Text } from '@krea/common/shared-components/text';
import { useGetCachedCurrentLoanApplication } from '@krea/common/store/current-loan-application/hooks';
import {
  useIsFieldRequestSubmitting,
  usePostFieldRequest,
} from '@krea/common/store/field-requests/hooks/mutations';
import {
  useGetFieldRequests,
  useGetOneFieldRequest,
} from '@krea/common/store/field-requests/hooks/queries';
import {
  COMPANY_TYPE,
  COUNTRY_CODE,
  ScrollToElement,
} from '@krea/common/utils';

import FieldRequestPreviewHeader from '../FieldRequestPreviewHeader';
import FieldRequestSaved from '../FieldRequestSaved';
import { FieldRequestForm } from '../GetFieldRequestForm';

import { INIT_TYPE } from './AccountingDataForm';
import styles from './SelfEstimateHistoricRevenueForm.module.scss';

const VERIFICATION_FR_TYPES = {
  ACCOUNTING_DATA: 'accountingData',
  BANKING_DATA: 'bankingData',
};

const getVerificationOptions = (t) => {
  return [
    {
      id: `${VERIFICATION_FR_TYPES.ACCOUNTING_DATA}-${INIT_TYPE.FILES}`,
      title: t(
        'iaq.selfEstimatedHistoricRevenue.verificationOptions.accountingFiles.title',
      ),
      initType: INIT_TYPE.FILES,
    },
    ...(settings.countryCode === COUNTRY_CODE.SE
      ? [
          {
            id: `${VERIFICATION_FR_TYPES.ACCOUNTING_DATA}-${INIT_TYPE.FORTNOX}`,
            title: 'Fortnox',
            text: t('iaq.selfEstimatedHistoricRevenue.automaticFetching'),
            initType: INIT_TYPE.FORTNOX,
          },
          {
            id: `${VERIFICATION_FR_TYPES.ACCOUNTING_DATA}-${INIT_TYPE.VISMA_E_ECONOMY}`,
            title: 'Visma eEkonomi',
            text: t('iaq.selfEstimatedHistoricRevenue.automaticFetching'),
            initType: INIT_TYPE.VISMA_E_ECONOMY,
          },
        ]
      : []),
    {
      id: VERIFICATION_FR_TYPES.BANKING_DATA,
      title: t(
        'iaq.selfEstimatedHistoricRevenue.verificationOptions.bankingData.title',
      ),
      text: t('iaq.selfEstimatedHistoricRevenue.automaticFetching'),
      initType: null,
    },
  ];
};

const getAccountingAndBankingDataFRIDs = (fieldRequests) => {
  const accountingDataFieldRequestID = fieldRequests.find(({ field }) => {
    return field === VERIFICATION_FR_TYPES.ACCOUNTING_DATA;
  })?.id;

  const bankingDataFieldRequestID = fieldRequests.find(({ field }) => {
    return field === VERIFICATION_FR_TYPES.BANKING_DATA;
  })?.id;

  return { accountingDataFieldRequestID, bankingDataFieldRequestID };
};

const SelfEstimatedHistoricRevenue = ({
  fieldRequestID,
  withInputLabel,
  isPreviewMode,
}) => {
  const { t } = useTranslation();
  const isMobile = useMobile();
  const [formattedCurrencyValue, currencyValue] = useFormatCurrency({
    currency: settings.currency,
  });

  const [showVerificationOptions, setShowVerificationOptions] = useState(false);
  const [selectedVerificationOptionID, setSelectedVerificationOptionID] =
    useState(null);

  const { data: allFieldRequests = [] } = useGetFieldRequests();
  const { accountingDataFieldRequestID, bankingDataFieldRequestID } =
    getAccountingAndBankingDataFRIDs(allFieldRequests);
  const verificationOptions = getVerificationOptions(t);

  const { data: currentFieldRequest = {} } =
    useGetOneFieldRequest(fieldRequestID);
  const { data: accountingDataFieldRequest = {} } = useGetOneFieldRequest(
    accountingDataFieldRequestID,
  );
  const { data: bankingDataFieldRequest = {} } = useGetOneFieldRequest(
    bankingDataFieldRequestID,
  );
  const { data: currentLoanApplication = {} } =
    useGetCachedCurrentLoanApplication();
  const { mutate: postFieldRequest, error: err } =
    usePostFieldRequest(fieldRequestID);

  const error = err?.response?.data?.errors?.[0];

  const shouldVerifyRevenues = () => {
    const companyType =
      currentLoanApplication.applyingCompany?.customerCompany?.typeDisplayName;
    const requestedLoanAmount = parseInt(
      currentLoanApplication.requestedLoanAmount,
    );

    const isCompanyTypeLimitedOrPartnership = [
      COMPANY_TYPE.LIMITED_COMPANY,
      COMPANY_TYPE.PARTNERSHIP,
    ].includes(companyType);

    const hasRequestedAmountThreshhold =
      (settings.countryCode === COUNTRY_CODE.SE &&
        requestedLoanAmount <= 600_000) ||
      (settings.countryCode === COUNTRY_CODE.FI &&
        requestedLoanAmount <= 60_000);

    return !isCompanyTypeLimitedOrPartnership || !hasRequestedAmountThreshhold;
  };

  const isCurrentFRSubmitting = useIsFieldRequestSubmitting(fieldRequestID);
  const isAccountingDataFRSubmitting = useIsFieldRequestSubmitting(
    accountingDataFieldRequestID,
  );
  const isBankingDataFRSubmitting = useIsFieldRequestSubmitting(
    bankingDataFieldRequestID,
  );

  const [touched, setTouched] = useState(false);
  const [value, setValue] = useState(currentFieldRequest?.submittedData);

  const isAnyFRSubmitting =
    isCurrentFRSubmitting ||
    isAccountingDataFRSubmitting ||
    isBankingDataFRSubmitting;

  const hasAnySubmittedVerificationFR =
    accountingDataFieldRequest?.isSubmitted ||
    bankingDataFieldRequest?.isSubmitted;
  const isAnySubmitted =
    currentFieldRequest?.isSubmitted &&
    (hasAnySubmittedVerificationFR || !showVerificationOptions);

  const onPostValueFn = (newValue) => {
    setTouched(false);

    postFieldRequest({ value: newValue });
  };

  const onKeyDown = (e) => {
    if (e.key === 'Enter') {
      e.preventDefault();
      onPostValueFn(value);
    }
  };

  // eslint-disable-next-line react-hooks/exhaustive-deps
  const debouncedSave = useCallback(
    debounce((newValue) => onPostValueFn(newValue), 500),
    [],
  );

  const onChange = ({ target }) => {
    const parsedValue = parseInt(target.value.replace(/\s+/g, ''), 10);
    const newValue = Number.isInteger(parsedValue) ? parsedValue : null;

    setTouched(true);
    setValue(newValue);
    debouncedSave(newValue);
  };

  const onBlur = () => {
    if (touched) {
      debouncedSave.cancel();
      onPostValueFn(value);
    }
  };

  const openVerificationOptions = () => {
    setShowVerificationOptions(!showVerificationOptions);

    if (showVerificationOptions) {
      setSelectedVerificationOptionID(null);
    } else if (accountingDataFieldRequest?.isSubmitted) {
      const { erp_system } = accountingDataFieldRequest?.submittedData;

      if (erp_system === 'OTHER') {
        setSelectedVerificationOptionID(
          `${VERIFICATION_FR_TYPES.ACCOUNTING_DATA}-${INIT_TYPE.FILES}`,
        );
      } else if (erp_system === 'fortnox') {
        setSelectedVerificationOptionID(
          `${VERIFICATION_FR_TYPES.ACCOUNTING_DATA}-${INIT_TYPE.FORTNOX}`,
        );
      } else if (erp_system === 'visma') {
        setSelectedVerificationOptionID(
          `${VERIFICATION_FR_TYPES.ACCOUNTING_DATA}-${INIT_TYPE.VISMA_E_ECONOMY}`,
        );
      }
    } else if (bankingDataFieldRequest?.isSubmitted) {
      setSelectedVerificationOptionID(VERIFICATION_FR_TYPES.BANKING_DATA);
    }
  };

  useEffect(() => {
    if (!currentFieldRequest?.isSubmitted) {
      setShowVerificationOptions(false);
      setSelectedVerificationOptionID(null);
    }
  }, [currentFieldRequest?.isSubmitted]);

  return isPreviewMode ? (
    <FieldRequestPreviewHeader
      label={currentFieldRequest?.displayName}
      value={formattedCurrencyValue(value)}
    />
  ) : (
    <>
      <form
        className={classnames('input-form', {
          'input-form-padded': isPreviewMode,
        })}
        onKeyDown={onKeyDown}
        onSubmit={(e) => e.preventDefault()}
      >
        <TextInput
          type="text"
          pattern="[0-9]*"
          maxLength="15"
          inputMode="numeric"
          id={`input_${currentFieldRequest?.field}`}
          value={value?.toLocaleString('sv') || ''}
          onChange={onChange}
          onBlur={onBlur}
          placeholder={t('iaq.amount.valuePlaceholder')}
          textAppend={currencyValue}
          label={withInputLabel && currentFieldRequest?.displayName}
          description={currentFieldRequest?.displayText}
          error={error}
        />

        <FieldRequestSaved
          isSubmitting={isAnyFRSubmitting}
          isSubmitted={isAnySubmitted}
        />
      </form>

      {verificationOptions &&
        currentFieldRequest?.isSubmitted &&
        shouldVerifyRevenues() && (
          <div className={styles.verificationOptions}>
            <div className={styles.showOptionsBadge}>
              <Text className={styles.badgeText}>
                <Trans
                  i18nKey="iaq.selfEstimatedHistoricRevenue.verifyRevenueTitle"
                  components={{
                    span: <span />,
                    helper: (
                      <Helper
                        className={styles.helper}
                        content={t(
                          'iaq.selfEstimatedHistoricRevenue.helperText',
                        )}
                        useQuestionIcon={true}
                      />
                    ),
                  }}
                />
              </Text>

              <Button
                variant="text"
                size="sm"
                onClick={openVerificationOptions}
                disabled={isCurrentFRSubmitting}
              >
                {showVerificationOptions
                  ? t('iaq.close')
                  : t('iaq.selfEstimatedHistoricRevenue.verify')}
              </Button>
            </div>

            {showVerificationOptions && (
              <RadioGroup
                value={selectedVerificationOptionID}
                className={styles.radioGroup}
              >
                {verificationOptions.map(({ id, title, text, initType }) => {
                  const verificationFieldRequestID =
                    id === VERIFICATION_FR_TYPES.BANKING_DATA
                      ? bankingDataFieldRequestID
                      : accountingDataFieldRequestID;

                  return (
                    <div key={id} className={styles.outerDiv}>
                      <Radio
                        id={id}
                        value={id}
                        name={id}
                        onClick={({ target }) => {
                          if (isMobile) {
                            ScrollToElement('fieldRequestForm');
                          }
                          setSelectedVerificationOptionID(target.value);
                        }}
                        checked={selectedVerificationOptionID === id}
                        readOnly={true}
                        className={classnames(styles.radio, {
                          'bg-gray': selectedVerificationOptionID === id,
                        })}
                        iconClassName={styles.radioIconClass}
                        label={
                          <div className={styles.label}>
                            {title}{' '}
                            {text && (
                              <span className="font-weight-normal">
                                - {text}
                              </span>
                            )}
                          </div>
                        }
                      />

                      {selectedVerificationOptionID === id && (
                        <div
                          name="fieldRequestForm"
                          className={classnames(
                            styles.fieldRequestDiv,
                            'pb-2',
                            {
                              'pb-4':
                                id === VERIFICATION_FR_TYPES.ACCOUNTING_DATA,
                              'pb-2': id === VERIFICATION_FR_TYPES.BANKING_DATA,
                            },
                          )}
                        >
                          <FieldRequestForm
                            fieldRequestID={verificationFieldRequestID}
                            initType={initType}
                            hasParentFieldRequest={true}
                          />
                        </div>
                      )}
                    </div>
                  );
                })}
              </RadioGroup>
            )}
          </div>
        )}
    </>
  );
};

export default SelfEstimatedHistoricRevenue;
